import { Button, Cell, styled, TextInput } from '@zeiss/beyond-online-react';
import { Box, Grid } from '@zeiss/lib-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useWizardForm } from 'src/modules/Invitations/InvitationCreate/form';
import {
  getAdditionalDataDuplicateKeySchema,
  getAdditionalDataKeyFieldSchema,
  getAdditionalDataValueFieldSchema,
} from 'src/modules/Invitations/InvitationCreate/schema';
import type {
  AdditionalDataEntry,
  StepComponent,
} from 'src/modules/Invitations/InvitationCreate/types';
import { InvitationType } from 'src/types/rest/managementPortalApi';

// Monkey-patch to get the helper container height without visible content
const emptyHelperTextPlaceholder = '\u00A0';

const getDuplicateAdditionalDataKeyIndices = (
  entries: AdditionalDataEntry[],
) => {
  // The array-level schema reports that duplicates exist; this UI pass marks
  // every offending row so both duplicated inputs can render inline errors.
  const indicesByKey = new Map<string, number[]>();

  entries.forEach(({ key }, index) => {
    const normalizedKey = key.trim();

    if (!normalizedKey.length) {
      return;
    }

    const indices = indicesByKey.get(normalizedKey) ?? [];
    indices.push(index);
    indicesByKey.set(normalizedKey, indices);
  });

  return new Set(
    Array.from(indicesByKey.values()).flatMap((indices) =>
      indices.length > 1 ? indices : [],
    ),
  );
};

const Entries = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-m);
`;

const EntryRow = styled.div`
  display: grid;
  gap: var(--spacing-s);

  @media (width >= 768px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    align-items: end;
  }
`;

const StyledActionButton = styled(Button)`
  justify-self: start;
  width: auto;
  padding-inline: 0;
`;

const StyledRemoveButton = styled(Button)`
  justify-self: start;
  width: auto;

  @media (width >= 768px) {
    align-self: end;
  }
`;

const StepDescription = styled.p`
  margin: 0;
  font: var(--text-body-m);
  color: var(--color-global-text-subtle);
`;

/**
 * StepAdditionalDataFields
 *
 * - TextInput `helperText` is applied if the other TextInput field in the same
 *   row has an error. This syncs their height.
 */
export const StepAdditionalDataFields: StepComponent = ({
  displayFieldError,
}) => {
  const { i18n, t } = useTranslation(['common', 'invitations']);
  const form = useWizardForm();

  const additionalDataDuplicateKeySchema = useMemo(
    () => getAdditionalDataDuplicateKeySchema(t),
    [i18n.language, t],
  );
  const additionalDataKeyFieldSchema = useMemo(
    () => getAdditionalDataKeyFieldSchema(t),
    [i18n.language, t],
  );
  const additionalDataValueFieldSchema = useMemo(
    () => getAdditionalDataValueFieldSchema(t),
    [i18n.language, t],
  );

  return (
    <Grid>
      <Cell.Basic colSpan={[4, 6, 11]}>
        <form.Subscribe selector={(state) => state.values.step1.invitationType}>
          {(invitationType) => (
            <>
              {invitationType ? (
                <Box>
                  <StepDescription>
                    {t(
                      invitationType === InvitationType.application
                        ? 'invitations:text.additionalData_application'
                        : 'invitations:text.additionalData_tenant',
                    )}
                  </StepDescription>
                </Box>
              ) : null}

              <form.Field
                mode="array"
                name="step3.additionalData"
                validators={{
                  onChange: additionalDataDuplicateKeySchema,
                  onSubmit: additionalDataDuplicateKeySchema,
                }}
              >
                {(field) => (
                  <form.Subscribe
                    selector={(state) => state.values.step3.additionalData}
                  >
                    {(additionalData) => {
                      const duplicateKeyIndices =
                        getDuplicateAdditionalDataKeyIndices(additionalData);

                      return (
                        <Box>
                          <Entries>
                            {additionalData.map((_, index) => (
                              <EntryRow key={index}>
                                <form.Field
                                  name={`step3.additionalData[${index}].key`}
                                  validators={{
                                    onBlur: additionalDataKeyFieldSchema,
                                    onChange: additionalDataKeyFieldSchema,
                                    onSubmit: additionalDataKeyFieldSchema,
                                  }}
                                >
                                  {(nameField) => (
                                    <form.Field
                                      name={`step3.additionalData[${index}].value`}
                                      validators={{
                                        onBlur: additionalDataValueFieldSchema,
                                        onChange:
                                          additionalDataValueFieldSchema,
                                        onSubmit:
                                          additionalDataValueFieldSchema,
                                      }}
                                    >
                                      {(valueField) => {
                                        const nameFieldError =
                                          displayFieldError(nameField) ??
                                          (duplicateKeyIndices.has(index)
                                            ? t(
                                                'invitations:error.additionalDataDuplicateKey',
                                              )
                                            : undefined);
                                        const valueFieldError =
                                          displayFieldError(valueField);

                                        return (
                                          <>
                                            <TextInput
                                              autoComplete="off"
                                              error={nameFieldError}
                                              helperText={
                                                !nameFieldError &&
                                                valueFieldError
                                                  ? emptyHelperTextPlaceholder
                                                  : undefined
                                              }
                                              id={nameField.name}
                                              label={t('common:name')}
                                              name={nameField.name}
                                              onBlur={nameField.handleBlur}
                                              onChange={(event) =>
                                                nameField.handleChange(
                                                  event.target.value,
                                                )
                                              }
                                              value={nameField.state.value}
                                            />

                                            <TextInput
                                              autoComplete="off"
                                              error={valueFieldError}
                                              helperText={
                                                !valueFieldError &&
                                                nameFieldError
                                                  ? emptyHelperTextPlaceholder
                                                  : undefined
                                              }
                                              id={valueField.name}
                                              label={t(
                                                'invitations:label.fieldValue',
                                              )}
                                              name={valueField.name}
                                              onBlur={valueField.handleBlur}
                                              onChange={(event) =>
                                                valueField.handleChange(
                                                  event.target.value,
                                                )
                                              }
                                              value={valueField.state.value}
                                            />
                                          </>
                                        );
                                      }}
                                    </form.Field>
                                  )}
                                </form.Field>

                                <StyledRemoveButton
                                  icon="Delete"
                                  onClick={() => field.removeValue(index)}
                                  variant="tertiary"
                                >
                                  {t('common:action.remove')}
                                </StyledRemoveButton>
                              </EntryRow>
                            ))}

                            <div>
                              <StyledActionButton
                                icon="Plus"
                                iconPosition="left"
                                onClick={() =>
                                  field.pushValue({ key: '', value: '' })
                                }
                                variant="tertiary"
                              >
                                {t('invitations:action.addDataField')}
                              </StyledActionButton>
                            </div>
                          </Entries>
                        </Box>
                      );
                    }}
                  </form.Subscribe>
                )}
              </form.Field>
            </>
          )}
        </form.Subscribe>
      </Cell.Basic>
    </Grid>
  );
};
